/**
 * Reading an ossia back out of a file.
 *
 * A file writes an ossia as a staff of the part -- `<staff-details><staff-type>ossia` --
 * placed immediately above the staff it offers an alternative to, which is how this
 * package writes one and what every program that knows the word does. The document has no
 * such staff: it keeps an ossia as a voice of the bar flagged {@link Voice.ossia}, sitting
 * on the staff it stands over. See `export-ossia.ts` for the way out.
 *
 * So the staves have to be renumbered on the way in, and that cannot happen while the bars
 * are being read: a bar names its staves by the file's numbers, and whether one of those is
 * an ossia is stated once, in the first `<attributes>`, for the whole part. The bars are
 * read as the file numbers them and mapped afterwards, once, here.
 *
 * A file with no ossia in it maps every staff to itself, which is every file written before
 * this existed.
 */

import type { Clef, MusicEvent, PartMeasure, Voice } from "../document/types";

/** How a file's staff numbers become the document's. */
export interface OssiaMap {
  /**
   * The document staff each of the file's staves belongs to, by the file's 0-based index.
   *
   * For an ossia staff this is the staff it stands over, which is the one it shares.
   */
  readonly staff: readonly number[];
  /** Whether each of the file's staves is an ossia staff. */
  readonly ossia: readonly boolean[];
  /** How many staves the part itself has, the ossia staves not counted. */
  readonly count: number;
}

/**
 * The mapping for a part, from the staves the file declared as ossias.
 *
 * An ossia takes the number of the staff below it, since that is the staff it stands over.
 * One written below the last staff of the part -- which nothing should write, but a file
 * may -- falls back to that last staff, so that a malformed file still reads as music.
 */
export function ossiaMap(ossiaStaves: ReadonlySet<number>, staffCount: number): OssiaMap {
  const staff: number[] = [];
  const ossia: boolean[] = [];
  let next = 0;
  for (let at = 0; at < staffCount; at += 1) {
    const isOssia = ossiaStaves.has(at);
    ossia.push(isOssia);
    staff.push(next);
    if (!isOssia) next += 1;
  }
  const count = Math.max(1, next);
  return { staff: staff.map((index) => Math.min(index, count - 1)), ossia, count };
}

/** Whether a part's staves are numbered exactly as the document numbers them. */
export function isPlain(map: OssiaMap): boolean {
  return map.ossia.every((flag) => !flag);
}

/** One of the file's staves as a document staff index. */
function mapped(map: OssiaMap, staff: number): number {
  return map.staff[staff] ?? Math.min(staff, map.count - 1);
}

/**
 * One bar with its staff numbers translated.
 *
 * A voice on an ossia staff becomes an ossia voice on the staff under it, and its notes
 * stop naming a staff of their own: an ossia is drawn on a staff of its own, and that staff
 * is the ossia's alone, so there is nowhere for a note in one to be drawn across to.
 */
export function remapMeasure(measure: PartMeasure, map: OssiaMap): PartMeasure {
  const voices = measure.voices.map((voice) => remapVoice(voice, map)).filter(offersSomething);
  const clefChanges: Record<number, Clef> = {};
  for (const [staff, clef] of Object.entries(measure.clefChanges)) {
    // The clef of an ossia staff is the clef of the staff it stands over, stated twice.
    if (map.ossia[Number(staff)] === true) continue;
    clefChanges[mapped(map, Number(staff))] = clef;
  }
  return { ...measure, voices, clefChanges };
}

/**
 * Whether a line is worth keeping.
 *
 * An ossia staff is written in every bar of the part that has one, because a reader takes
 * the staves of a bar in the order they arrive and a bar that skipped it would hand the
 * next staff's music to it. Where the part offers no alternative the staff holds a bar's
 * rest, and a bar's rest alone on an ossia staff is exactly that: nothing offered here.
 */
function offersSomething(voice: Voice): boolean {
  if (!voice.ossia) return true;
  const only = voice.events.length === 1 ? voice.events[0] : undefined;
  return only === undefined || only.kind !== "rest" || !only.wholeMeasure;
}

function remapVoice(voice: Voice, map: OssiaMap): Voice {
  const ossia = map.ossia[voice.staff] === true;
  const staff = mapped(map, voice.staff);
  const events: MusicEvent[] = voice.events.map((event) => {
    if (event.staff === null) return event;
    return { ...event, staff: ossia ? null : mapped(map, event.staff) };
  });
  return { ...voice, staff, events, ossia };
}
